import React, {useEffect, useState} from 'react'
import {Modal, Button, Form, FormControl, FormGroup, FormLabel, InputGroup} from 'react-bootstrap'
import {
  branchUsersFilterDataChange,
  create,
  selectBranchUsersList,
  selectShowAddBranchModal,
  selectTransactionLoading,
  setShowAddBranchModal,
} from '../redux/BranchRedux'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {useDispatch} from 'react-redux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {MAX_COMBOBOX_LOAD_ITEMS} from '../../../shared/constants'
import {getUserFullName} from '../../../shared/utils'
import {selectUser} from '../../../modules/auth'
const AddBranchModal: React.FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [showPassword, setShowPassword] = useState(false)
  const visible = useAppSelector(selectShowAddBranchModal)
  const users = useAppSelector(selectBranchUsersList)
  const userProfile = useAppSelector(selectUser)
  const isLoading = useAppSelector(selectTransactionLoading)
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(1, intl.formatMessage({id: 'BRANCH.NAME_MIN'}))
      .max(50, intl.formatMessage({id: 'BRANCH.NAME_MAX'}))
      .required(intl.formatMessage({id: 'BRANCH.NAME_REQUIRED'})),
    address: Yup.string()
      .min(1, intl.formatMessage({id: 'BRANCH.ADDRESS_MIN'}))
      .max(100, intl.formatMessage({id: 'BRANCH.ADDRESS_MAX'}))
      .required(intl.formatMessage({id: 'BRANCH.ADDRESS_REQUIRED'})),
    phone: Yup.string()
      .min(1, intl.formatMessage({id: 'BRANCH.PHONE_MIN'}))
      .max(15, intl.formatMessage({id: 'BRANCH.PHONE_MAX'}))
      .required(intl.formatMessage({id: 'BRANCH.PHONE_REQUIRED'})),
    postcode: Yup.string()
      .min(1, intl.formatMessage({id: 'BRANCH.POSTCODE_MIN'}))
      .max(10, intl.formatMessage({id: 'BRANCH.POSTCODE_MAX'}))
      .required(intl.formatMessage({id: 'BRANCH.POSTCODE_REQUIRED'})),
    maxAllowedCredit: Yup.number()
      .typeError(intl.formatMessage({id: 'BRANCH.MAX_ALLOWED_CREDIT_INVALID'}))
      .min(0, intl.formatMessage({id: 'BRANCH.MAX_ALLOWED_CREDIT_MIN'}))
      .when('isSeparateBilling', {
        is: false,
        then: Yup.number().required(intl.formatMessage({id: 'BRANCH.MAX_ALLOWED_CREDIT_REQUIRED'})),
      }),
    firstName: Yup.string()
      .min(1, intl.formatMessage({id: 'BRANCH.FIRST_NAME_MIN'}))
      .max(30, intl.formatMessage({id: 'BRANCH.FIRST_NAME_MAX'}))
      .when('autoCreate', {
        is: true,
        then: Yup.string().required(intl.formatMessage({id: 'BRANCH.FIRST_NAME_REQUIRED'})),
      }),
    lastName: Yup.string()
      .min(1, intl.formatMessage({id: 'BRANCH.LAST_NAME_MIN'}))
      .max(30, intl.formatMessage({id: 'BRANCH.LAST_NAME_MAX'}))
      .when('autoCreate', {
        is: true,
        then: Yup.string().required(intl.formatMessage({id: 'BRANCH.LAST_NAME_REQUIRED'})),
      }),
    email: Yup.string()
      .email(intl.formatMessage({id: 'BRANCH.EMAIL_INVALID'}))
      .min(5, intl.formatMessage({id: 'BRANCH.EMAIL_MIN'}))
      .max(50, intl.formatMessage({id: 'BRANCH.EMAIL_MAX'}))
      .when('autoCreate', {
        is: true,
        then: Yup.string().required(intl.formatMessage({id: 'BRANCH.EMAIL_REQUIRED'})),
      }),
    password: Yup.string()
      .min(8, intl.formatMessage({id: 'BRANCH.PASSWORD_MIN'}))
      .required(intl.formatMessage({id: 'BRANCH.PASSWORD_REQUIRED'})),
    personInChargeId: Yup.number().when('autoCreate', {
      is: false,
      then: Yup.number().required(intl.formatMessage({id: 'BRANCH.PERSON_IN_CHARGE_REQUIRED'})),
    }),
  })

  const generatePassword = (): string => {
    const length = 12
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+[]{}|;:,.<>?'
    let password = ''
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length)
      password += charset[randomIndex]
    }
    return password
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      address: '',
      phone: '',
      isSeparateBilling: false,
      personInChargeId: undefined,
      autoCreate: false,
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      password: generatePassword(),
      postcode: '',
      maxAllowedCredit: 0,
    },
    validationSchema,
    onSubmit: (values) => {
      const brandData = {
        ...values,
        personInCharge: values.autoCreate
          ? {
              firstName: values.firstName,
              lastName: values.lastName,
              title: values.title,
              email: values.email,
              password: values.password,
            }
          : undefined,
      }

      dispatch(create(brandData))
    },
  })
  useEffect(() => {
    if (visible) {
      formik.resetForm()
      formik.setFieldValue('password', generatePassword())
      dispatch(
        branchUsersFilterDataChange({
          page: 1,
          take: Number(MAX_COMBOBOX_LOAD_ITEMS),
          itemCount: 0,
          pageCount: 1,
        })
      )
    }
  }, [visible])
  return (
    <Modal
      show={visible}
      onHide={isLoading ? undefined : () => dispatch(setShowAddBranchModal(false))}
      centered
      backdrop={isLoading ? 'static' : true}
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>{intl.formatMessage({id: 'BRANCH.ADD_NEW'})}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'Registration.form.COMPANY_NAME'})}</FormLabel>
            <FormControl
              value={userProfile?.companyName || ''}
              disabled
              readOnly
              style={{backgroundColor: '#f8f9fa', color: '#6c757d'}}
            />
          </FormGroup>

          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.NAME'})}</FormLabel>
            <FormControl
              {...formik.getFieldProps('name')}
              isInvalid={formik.touched.name && !!formik.errors.name}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.name}</Form.Control.Feedback>
          </FormGroup>

          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.ADDRESS'})}</FormLabel>
            <FormControl
              {...formik.getFieldProps('address')}
              isInvalid={formik.touched.address && !!formik.errors.address}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.address}</Form.Control.Feedback>
          </FormGroup>

          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.PHONE'})}</FormLabel>
            <FormControl
              {...formik.getFieldProps('phone')}
              isInvalid={formik.touched.phone && !!formik.errors.phone}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.phone}</Form.Control.Feedback>
          </FormGroup>

          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.POSTCODE'})}</FormLabel>
            <FormControl
              {...formik.getFieldProps('postcode')}
              isInvalid={formik.touched.postcode && !!formik.errors.postcode}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.postcode}</Form.Control.Feedback>
          </FormGroup>

          <FormGroup className='mb-3 mt-5'>
            <Form.Check
              type='checkbox'
              id='isSeparateBillingCheckbox'
              label={intl.formatMessage({id: 'BRANCH.SEPARATE_BILL'})}
              {...formik.getFieldProps('isSeparateBilling')}
            />
            <div className='form-text text-muted mt-2'>
              <i className='bi bi-info-circle me-2'></i>
              {intl.formatMessage({id: 'BRANCH.SEPARATE_BILL_NOTE'})}
            </div>
          </FormGroup>
          {!formik.values.isSeparateBilling && (
            <FormGroup className='mb-3'>
              <FormLabel>{intl.formatMessage({id: 'BRANCH.MAX_ALLOWED_CREDIT'})}</FormLabel>
              <FormControl
                type='number'
                {...formik.getFieldProps('maxAllowedCredit')}
                isInvalid={formik.touched.maxAllowedCredit && !!formik.errors.maxAllowedCredit}
              />
              <Form.Control.Feedback type='invalid'>
                {formik.errors.maxAllowedCredit}
              </Form.Control.Feedback>
            </FormGroup>
          )}
          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.PERSON_IN_CHARGE'})}</FormLabel>
            <FormControl
              as='select'
              disabled={formik.values.autoCreate}
              {...formik.getFieldProps('personInChargeId')}
              isInvalid={formik.touched.personInChargeId && !!formik.errors.personInChargeId}
            >
              <option value={undefined}>
                {intl.formatMessage({id: 'BRANCH.SELECT_PERSON_IN_CHARGE'})}
              </option>
              {users
                .filter((user) => user.isPersonInCharge === false)
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {getUserFullName(user, intl.locale)}
                  </option>
                ))}
            </FormControl>
            <Form.Control.Feedback type='invalid'>
              {formik.errors.personInChargeId}
            </Form.Control.Feedback>
          </FormGroup>

          <FormGroup className='mb-3'>
            <Form.Check
              type='switch'
              id='autoCreateSwitch'
              label={intl.formatMessage({id: 'BRANCH.AUTO_CREATE_PERSON'})}
              checked={formik.values.autoCreate}
              onChange={() => formik.setFieldValue('autoCreate', !formik.values.autoCreate)}
            />
          </FormGroup>

          {formik.values.autoCreate && (
            <>
              <FormGroup className='mb-3'>
                <FormLabel>{intl.formatMessage({id: 'BRANCH.FIRST_NAME'})}</FormLabel>
                <FormControl
                  {...formik.getFieldProps('firstName')}
                  isInvalid={formik.touched.firstName && !!formik.errors.firstName}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.firstName}
                </Form.Control.Feedback>
              </FormGroup>

              <FormGroup className='mb-3'>
                <FormLabel>{intl.formatMessage({id: 'BRANCH.LAST_NAME'})}</FormLabel>
                <FormControl
                  {...formik.getFieldProps('lastName')}
                  isInvalid={formik.touched.lastName && !!formik.errors.lastName}
                />
                <Form.Control.Feedback type='invalid'>
                  {formik.errors.lastName}
                </Form.Control.Feedback>
              </FormGroup>

              <FormGroup className='mb-3'>
                <FormLabel>{intl.formatMessage({id: 'BRANCH.TITLE'})}</FormLabel>
                <FormControl {...formik.getFieldProps('title')} />
              </FormGroup>

              <FormGroup className='mb-3'>
                <FormLabel>{intl.formatMessage({id: 'BRANCH.EMAIL'})}</FormLabel>
                <FormControl
                  type='email'
                  {...formik.getFieldProps('email')}
                  isInvalid={formik.touched.email && !!formik.errors.email}
                />
                <Form.Control.Feedback type='invalid'>{formik.errors.email}</Form.Control.Feedback>
              </FormGroup>

              <FormGroup className='mb-3'>
                <FormLabel>{intl.formatMessage({id: 'BRANCH.PASSWORD'})}</FormLabel>
                <div className='d-flex'>
                  <InputGroup>
                    <FormControl
                      type={showPassword ? 'text' : 'password'}
                      {...formik.getFieldProps('password')}
                    />

                    <Button
                      variant='outline-secondary'
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        fill='currentColor'
                        className='bi bi-eye-slash'
                        viewBox='0 0 16 16'
                      >
                        <path d='M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7 7 0 0 0-2.79.588l.77.771A6 6 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755q-.247.248-.517.486z' />
                        <path d='M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829' />
                        <path d='M3.35 5.47q-.27.24-.518.487A13 13 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7 7 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12z' />
                      </svg>
                    </Button>
                  </InputGroup>
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.email}
                  </Form.Control.Feedback>

                  <Button
                    variant='outline-secondary'
                    onClick={() => {
                      navigator.clipboard.writeText(formik.values.password)
                      toast.info(intl.formatMessage({id: 'BRANCH.PASSWORD_COPIED'}))
                    }}
                  >
                    <i className='bi bi-clipboard'></i>
                  </Button>
                </div>
              </FormGroup>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => dispatch(setShowAddBranchModal(false))}
            disabled={isLoading}
          >
            {intl.formatMessage({id: 'SYSTEM.CANCEL'})}
          </Button>
          <Button variant='primary' type='submit' disabled={isLoading}>
            {!isLoading && intl.formatMessage({id: 'SYSTEM.SAVE'})}
            {isLoading && (
              <>
                <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                {intl.formatMessage({id: 'Login.form.WAITING'})}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default AddBranchModal
